import { TooltipIcon } from '@ipfabric/ui-components/Tooltip/TooltipIcon';
import type { settingTypes } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { FormikMultiTextField } from 'components/FormikMultiTextField/FormikMultiTextField';
import { FormikTextAreaField } from 'components/FormikTextAreaField/FormikTextAreaField';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, type FormikProps, useFormik } from 'formik';
import { type FC, memo } from 'react';
import { validateSlug } from 'utils/formValidation/validations';
import type { FormikValueAdapter } from 'utils/formik/FormikValueAdapter';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { VendorDialogContainer } from './VendorDialogContainer';
import { FormikAdvancedSettings } from './fields/FormikAdvancedSettings';
import { FormikRateLimitingSettingsFields } from './fields/FormikRateLimitingSettingsFields';
import { FormikVendorApiCommentField } from './fields/FormikVendorApiCommentField';
import { FormikVendorApiEnabledField } from './fields/FormikVendorApiEnabledField';
import { FormikVendorApiRespectSystemProxyConfigurationField } from './fields/FormikVendorApiRejectUnauthorizedField';
import { FormikVendorApiSlugField } from './fields/FormikVendorApiSlugField';
import { getAdvancedSettingsDefaultValues } from './helpers/applyDefaultInitialValues';
import { isVendorUpdate } from './helpers/guardTypes';
import type { BaseVendorFormValues, VendorDialogProps } from './vendorTypes';

const resetSecretAttributes = (values: AwsFormValues): AwsFormValues => {
  return {
    ...values,
    apiKey: '',
    apiSecret: '',
  };
};

export type AwsFormValues = BaseVendorFormValues<settingTypes.AwsApiSettings>;

type Props = VendorDialogProps<
  AwsFormValues,
  settingTypes.AwsApiSettings,
  settingTypes.UpdateAwsApiSettings
>;

const stringInputToAssumeRole: FormikValueAdapter<
  string,
  AwsFormValues['assumeRoles']
> = {
  toInput: (value) => value?.map(prop('role')).join('\n') ?? '',
  toModel: (value) => value.split(/\r?\n/).map((role) => ({ role })),
};

const hasAssumeRoleDuplicates = (values: AwsFormValues) => {
  if (!values.assumeRoles) {
    return false;
  }
  const nonEmptyAssumeRoles = values.assumeRoles
    ?.map(prop('role'))
    .filter((x) => x !== '');

  return new Set(nonEmptyAssumeRoles).size < nonEmptyAssumeRoles.length;
};

const BareAwsForm: FC<Props> = (props) => {
  const editMode = props.dialogType === 'edit';
  const msg = useLocale();
  const {
    discoverySettings: {
      vendorsApi: { awsForm: awsFormMsg },
    },
  } = msg;
  const formik = useFormik<AwsFormValues>({
    initialValues: resetSecretAttributes(props.initialValues),
    validate: (values) => {
      const { required, duplicateAssumeRoles } = awsFormMsg.validation;
      const errors: FormikErrors<AwsFormValues> = {};

      if (!editMode && !values.apiKey) {
        errors.apiKey = required;
      }
      if (!editMode && !values.apiSecret) {
        errors.apiSecret = required;
      }
      if (values.regions.length === 0) {
        errors.regions = required;
      }
      if (hasAssumeRoleDuplicates(values)) {
        errors.assumeRoles = duplicateAssumeRoles;
      }

      validateSlug(values, errors, msg);

      return errors;
    },
    onSubmit: makeFormikOnSubmit((values: AwsFormValues) => {
      const formattedValues = {
        ...values,
        ...getAdvancedSettingsDefaultValues(values),
      };

      if (isVendorUpdate(values) && props.dialogType === 'edit') {
        props.onSubmit({
          ...formattedValues,
          id: values.id,
          apiKey: values.apiKey || props.initialValues.apiKey || undefined,
          apiSecret:
            values.apiSecret || props.initialValues.apiSecret || undefined,
        });
      } else if (props.dialogType === 'add') {
        props.onSubmit(formattedValues);
      }
    }),
  });

  return (
    <VendorDialogContainer
      formik={formik as FormikProps<AwsFormValues>}
      dialogType={props.dialogType}
      {...props.vendorDialogContainerPassthruProps}
    >
      <FormikVendorApiEnabledField formik={formik} />
      <FormikTextField
        autoFocus
        formik={formik}
        label={awsFormMsg.apiKey}
        placeholder={editMode ? '(unchanged)' : undefined}
        name='apiKey'
        type='text'
      />
      <FormikTextField
        formik={formik}
        label={awsFormMsg.apiSecret}
        placeholder={editMode ? '(unchanged)' : undefined}
        name='apiSecret'
        type='password'
      />
      <FormikMultiTextField
        formik={formik}
        label={awsFormMsg.regions}
        name='regions'
      />
      <FormikTextAreaField
        className='h-24'
        formik={formik}
        label={
          <>
            {awsFormMsg.assumeRoles}
            <TooltipIcon
              content={awsFormMsg.assumeRolesTooltip}
              iconProps={{
                iconStyle: 'solid',
                inline: true,
              }}
              className='ml-2'
              size='sm'
            />
          </>
        }
        name='assumeRoles'
        transformValue={stringInputToAssumeRole}
      />
      <FormikVendorApiSlugField formik={formik} />
      <FormikVendorApiCommentField formik={formik} />
      <FormikVendorApiRespectSystemProxyConfigurationField formik={formik} />
      <FormikAdvancedSettings>
        <FormikRateLimitingSettingsFields formik={formik} />
      </FormikAdvancedSettings>
    </VendorDialogContainer>
  );
};

export const AwsForm = memo(BareAwsForm);
